import { Api } from "api";
import { useWebsocket } from "components/shared/web-socket/WebsocketProvider";
import {
  FormItemType,
  ItemDataResult,
  NodeData,
  NodeFormData,
  NodeItem,
  NodeStatus,
} from "elza-api";
import { useCallback, useEffect, useRef, useState } from "react";
import { EventType } from "typings/websocket";
import { AnyMessage } from "typings/websocket/Message";
import { useAppSelector } from "utils/hooks/useAppSelector";
import { FormItem, getForcedItemTypes, useKeyGen } from "../item-form/formItems";
import {
  clearLocalStorageItemValues,
  clearStaleNewItemValues,
  createEmptyDescItem,
  moveLocalStorageItemValues,
} from "../item-form/desc-items/utils";
import { consumePendingTemplateCallback } from "./pendingTemplateItems";

function useWSNodeChanges(nodeId: number, callback: (version: number) => void) {
  const { addListener, removeListener } = useWebsocket();

  const handleMessage = (message: AnyMessage) => {
    if (
      message.eventType === EventType.NODES_CHANGE &&
      message.entityIds.includes(nodeId)
    ) {
      callback(message.versionId);
    }
    if (
        message.eventType === EventType.VISIBLE_POLICY_CHANGE
        && message.nodeIds.includes(nodeId)
    ) {
        callback(message.versionId);
    }
  };

  useEffect(() => {
    const listener = addListener(handleMessage);

    return () => {
      removeListener(listener);
    };
  }, []);
}

function convertToFormItems(
  items: NodeItem[],
  oldItems: FormItem[],
  generateLocalId: (item: NodeItem) => string,
  compare: (oldItem: NodeItem, newItem: NodeItem) => boolean,
): FormItem[] {
  return items.map((item) => {
    const oldItem = oldItems.find(({ item: oldItem }) => {
      return compare(oldItem, item);
      // return item.itemObjectId === oldItem.itemObjectId;
    });

    if (oldItem) {
      return {
        ...oldItem,
        item,
      };
    } else {
      return {
        localId: generateLocalId(item),
        item,
      };
    }
  });
}

export function useNodeFormData(
  fondsVersionId: number,
  nodeId: number,
  nodeVersionId?: number,
  options?: {
    skipForcedItems?: boolean;
    /**
     * If true, the hook skips its own initial fetch and waits for `seedFormData`/`seedNodeStatus`
     * to arrive via props from the parent. Refresh events (websocket NODES_CHANGE / VISIBLE_POLICY_CHANGE)
     * call `onRefresh` instead of fetching directly, so the parent can re-fetch and re-seed
     * (keeps accordion titles in sync with form edits).
     */
    seedFromParent?: boolean;
    seedFormData?: NodeFormData;
    seedNodeStatus?: NodeStatus;
    /** Called when seed mode requests a refresh. Parent should re-fetch and pass updated seed back. */
    onRefresh?: () => void;
  },
) {
  const itemTypeRefs = useAppSelector(
    ({ refTables }) => refTables.descItemTypes.itemsMap,
  );
  const dataTypeRefs = useAppSelector(
    ({ refTables }) => refTables.rulDataTypes.itemsMap,
  );

  const { getKey } = useKeyGen(nodeId);
  const pendingConsumedRef = useRef(false);

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [formData, setFormData] = useState<NodeFormData>();
  const [formItems, setFormItems] = useState<FormItem[]>([]);
  const [forcedFormItems, setForcedFormItems] = useState<FormItem[]>([]);
  const [addedFormItems, setAddedFormItems] = useState<FormItem[]>([]);
  const [arrPerm, setArrPerm] = useState<boolean>(false);
  const [itemTypes, setItemTypes] = useState<FormItemType[]>([]);
  const [nodeData, setNodeData] = useState<NodeStatus>();
  const seedFromParent = !!options?.seedFromParent;
  const seedFormData = options?.seedFormData;
  const seedNodeStatus = options?.seedNodeStatus;
  const onRefresh = options?.onRefresh;
  // Skip the initial fetch when the parent will seed the data.
  const [reloadData, setReloadData] = useState<boolean>(!seedFromParent);
  const [markedForClean, setMarkedForClean] = useState<
    { id: number; localId: string }[]
  >([]);
  const [storedData, setStoredData] = useState<NodeData>();

  const applyStoredData = useCallback(
    function (data: NodeData) {
      const _forcedDescItems = options?.skipForcedItems
        ? []
        : getForcedItemTypes(
            [
                ...(data.formData.descItems || []),
                ...addedFormItems.map(({ item }) => item)  // prevent forcing user added descItems
            ],
            data.formData.itemTypes,
            itemTypeRefs,
            dataTypeRefs,
            nodeId,
            nodeVersionId,
            { skipForcedItems: false },
          );

      // add desc items from template used to create the node
      if (!pendingConsumedRef.current) {
          pendingConsumedRef.current = true;
          const pendingCallback = consumePendingTemplateCallback(nodeId);
          if (pendingCallback) {
              // prevent adding empty items for already existing/forced
              const existingTypeIds = [
                  ...(data.formData.descItems || []),
                  ...(_forcedDescItems || [])
              ]?.map(({ itemTypeId }) => itemTypeId);
              pendingCallback((typeId, specId) => {
                  if (!existingTypeIds.includes(typeId)) {
                      return addEmptyDescItem(typeId, specId);
                  }
                  return '';
              });
          }
      }

      // setStoredData(undefined);
      setFormData(data.formData);
      setItemTypes(data.formData.itemTypes);
      setFormItems((prevFormItems) => {
        return convertToFormItems(
          data.formData.descItems,
          prevFormItems,
          (item) => {
            const marked = markedForClean.find(
              ({ id }) => id === item.itemObjectId,
            );
            if (marked) {
              return marked.localId;
            }
            return getKey();
          },
          (oldItem, newItem) => oldItem.itemObjectId === newItem.itemObjectId,
        );
      });
      setForcedFormItems((prevForcedFormItems) => {
        return convertToFormItems(
          _forcedDescItems,
          prevForcedFormItems,
          () => getKey(),
          (oldItem, newItem) =>
            oldItem.itemTypeId === newItem.itemTypeId &&
            oldItem.itemSpecId === newItem.itemSpecId,
        );
      });
      setArrPerm(data.formData.arrPerm);
      setNodeData(data.node);
      const survivingItems = addedFormItems.filter(({ localId }) => {
        return !markedForClean.find(
          ({ localId: _localId }) => localId === _localId,
        );
      });

      // Recompute the position of each locally added empty item so it sits after
      // the items currently known to the server (and any forced items) of the same
      // type. Their stored position can otherwise go stale when background changes
      // (copy from sibling, another user's edits) introduce new server items.
      const otherItems = [
        ...(data.formData.descItems || []),
        ..._forcedDescItems,
      ];
      const maxPositionByType = new Map<number, number>();
      for (const otherItem of otherItems) {
        const currentMax = maxPositionByType.get(otherItem.itemTypeId) ?? 0;
        maxPositionByType.set(
          otherItem.itemTypeId,
          Math.max(currentMax, otherItem.position),
        );
      }

      const repositionedItems = survivingItems.map((formItem) => {
        const nextPosition =
          (maxPositionByType.get(formItem.item.itemTypeId) ?? 0) + 1;
        maxPositionByType.set(formItem.item.itemTypeId, nextPosition);
        return { ...formItem, item: { ...formItem.item, position: nextPosition } };
      });

      // The position is part of the storage key of an unsaved item, so the pending values have
      // to follow the items to their new keys.
      moveLocalStorageItemValues(
        survivingItems.map(({ item }, index) => ({
          fromItem: item,
          toItem: repositionedItems[index].item,
        })),
      );

      clearStaleNewItemValues(nodeId, [
        ..._forcedDescItems,
        ...repositionedItems.map(({ item }) => item),
      ]);

      setAddedFormItems(repositionedItems);
    },
    [
      addedFormItems,
      dataTypeRefs,
      itemTypeRefs,
      markedForClean,
      nodeId,
      nodeVersionId,
      options?.skipForcedItems,
      getKey,
    ],
  );

  const fetchAndStoreData = useCallback(
    async function () {
      const { data } = await Api.node.nodeGetNodeData({
        fundVersionId: fondsVersionId,
        nodeId,
        formData: true,
        parents: false,
        children: false,
        siblingsMaxCount: 10,
        nodeStatus: true,
      });
      setStoredData(data);
    },
    [fondsVersionId, nodeId],
  );

  useEffect(() => {
    pendingConsumedRef.current = false;
  }, [nodeId]);

  useEffect(() => {
    if (reloadData && !seedFromParent) {
      fetchAndStoreData();
      setReloadData(false);
    }
  }, [fondsVersionId, nodeId, reloadData, seedFromParent, fetchAndStoreData]);

  // Apply parent-provided seed when it arrives (and matches the current nodeId).
  // Routes through setStoredData → the existing isSaving/storedData sync effect handles the rest.
  useEffect(() => {
    if (!seedFromParent || !seedFormData) {
      return;
    }
    if (seedFormData.parent?.id !== nodeId) {
      return; // stale seed for a different node — wait for matching seed
    }
    setStoredData({
      formData: seedFormData,
      node: seedNodeStatus,
    } as NodeData);
  }, [seedFromParent, seedFormData, seedNodeStatus, nodeId]);

  useEffect(() => {
    // Serves for synchronizing desc item Create request with NODES_CHANGE event from websocket
    // When the NODES_CHANGE event is received before the response from the Create request, the data is stored and applied
    // after the Create request response is received. If there are multiple NODES_CHANGE events, only the data received last are stored.
    // When storedData change (fetched new data), check whether the form is expecting a response
    // from Create request (isSaving = true), if not, use the stored data.
    // When server finishes saving and returns a response (isSaving > false), use the stored data
    if (!isSaving && storedData) {
      applyStoredData(storedData);
      setStoredData(undefined); // clear stored data
    }
  }, [isSaving, storedData, applyStoredData]);

  useWSNodeChanges(nodeId, () => {
    // In seed mode the parent owns the data lifecycle so it can also refresh accordion titles
    // that depend on the same response. Delegate to the parent's refresh; the new seed will
    // re-enter via the seedFormData/seedNodeStatus props.
    if (seedFromParent) {
      onRefresh?.();
    } else {
      fetchAndStoreData();
    }
  });

  const ws = useWebsocket();

  function addDescItem(item: NodeItem): string {
    const localId = getKey();
    setAddedFormItems((prev) => [...prev, { localId, item }]);
    return localId;
  }

  function addEmptyDescItem(typeId: number, specId?: number, position: number = 1): string {
    const typeRef = itemTypeRefs[typeId];
    if (!typeRef) {
      throw `Could not find type ref for id: ${typeId}`;
    }
    const dataType = dataTypeRefs?.[typeRef.dataTypeId];
    if (!dataType) {
      throw `Could not find data type ref for id: ${typeRef.dataTypeId}`;
    }
    if (nodeVersionId == undefined) {
      throw "'NodeVersionId' missing";
    }
    return addDescItem(
        {
            ...createEmptyDescItem(
                typeRef.id,
                nodeId,
                nodeVersionId,
                position,
                dataType.code,
            ),
            itemSpecId: specId,
        }
    );
  }

  async function deleteDescItem(item: NodeItem, localId: string) {
    // Item has data, delete from server
    if (item.data?.dataId != undefined || item.undefined) {
      await Api.descItems.descItemDeleteDescItem(fondsVersionId, {...item, data: undefined});
      clearLocalStorageItemValues(item);
      return;
    }

    // When item is missing dataId, item is not saved on server and is only deleted locally.
    // First check in user added items
    let index = addedFormItems.findIndex(
      ({ localId: _localId }) => _localId == localId,
    );
    if (index >= 0) {
      const _descItems = [...addedFormItems];
      _descItems.splice(index, 1);
      setAddedFormItems(_descItems);
      clearLocalStorageItemValues(item);
      return;
    }
    // If the item is not found in the added items, check the forced items
    // (currently not used, it is not possible to reset the value of forced items)
    const _descItems = [...forcedFormItems];
    index = forcedFormItems.findIndex(
      ({ localId: _localId }) => _localId == localId,
    );
    if (index >= 0) {
      const _descItem = _descItems[index].item;
      const emptyDescItem = createEmptyDescItem(
        _descItem.itemTypeId,
        _descItem.nodeId,
        _descItem.nodeVersion,
        _descItem.position,
        _descItem.data.dataType,
      );
      _descItems.splice(index, 1, { localId, item: emptyDescItem });
      setForcedFormItems(_descItems);
      clearLocalStorageItemValues(_descItem);
    }
    return;
  }

  // function markTypeForClean(localId: string, objectId: number) {
  //   setMarkedForClean([...markedForClean, { localId, id: objectId }]);
  // }

  async function createDescItem(item: NodeItem, localId: string) {
    setIsSaving(true);
    let _data: ItemDataResult;
    try {
      const response = await Api.descItems.descItemCreateDescItem(
        fondsVersionId,
        item,
      );
      _data = response?.data;
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
    if (_data) {
      // Drop the pending value by the item that was sent, not by the one that came back: the
      // key of an unsaved item holds its position and the server can create it on another.
      clearLocalStorageItemValues(item);

      setMarkedForClean([
        ...markedForClean,
        { localId, id: _data.item.itemObjectId },
      ]);

      return _data;
    } else {
      throw "Data missing";
    }
  }

  function updateDescItem(item: NodeItem, localId?: string) {
    if (!item.data?.dataId && localId) {
      const updateList = (prev: FormItem[]) =>
        prev.map(formItem => formItem.localId === localId ? { ...formItem, item } : formItem);
      setFormItems(updateList);
      setForcedFormItems(updateList);
      setAddedFormItems(updateList);
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      ws.send(
        `/app/arrangement/descItems/${fondsVersionId}/update/true`,
        JSON.stringify(item),
        () => {
          resolve();
        },
        // TODO Add type
        (error: unknown) => {
          reject(error);
        },
      );
    });
  }

  // Loading state — true while waiting for the parent's seed to arrive (or to match the current nodeId).
  // In fetch mode (no parent seed), `formData` toggles from undefined to populated on the API response;
  // we treat that as loading too so the consumer can show a spinner uniformly.
  const isLoading = seedFromParent
    ? !formData || formData.parent?.id !== nodeId
    : !formData;

  return {
    formData,
    formItems,
    forcedFormItems,
    addedFormItems,
    itemTypes,
    arrPerm,
    nodeData,
    isLoading,
    addDescItem,
    addEmptyDescItem,
    deleteDescItem,
    createDescItem,
    updateDescItem,
    parent: formData?.parent,
  };
}
